/**
 * Kit Builder — Serene Le Parfum
 * --------------------------------------------------
 * Variante B del A/B test para la colección "Crea tu kit de 50 ml".
 *
 * Componentes expuestos:
 *   window.KitBuilder          → state manager global (localStorage + events)
 *   <kit-add-button>           → botón "Sumar al kit" en la card
 *   <kit-quick-add-modal>      → modal slim que reemplaza el quick-add nativo
 *   <kit-progress-bar>         → barra sticky con contador + precio combo
 *   <kit-sticky-cta>           → CTA fijo inferior en mobile
 *
 * El AJAX usa /cart/add.js y luego /cart.js para refrescar el estado real.
 * Nada pisa el tema: todo está namespaced .kit-* y window.KitBuilder.
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------
  // 0a. COLA DE ESCRITURAS AL CARRITO (anti-race)
  // ---------------------------------------------------------------
  // Bug histórico "solo 2 de 3 fragancias entran al carrito": al tocar "Sumar
  // al kit" en 3 cards seguidas, se disparaban 3 POST /cart/add.js EN PARALELO.
  // Cada respuesta trae un snapshot server-side del cart-drawer del momento en
  // que Shopify lo procesó; la respuesta que llega de última pisa el innerHTML
  // del drawer y puede mostrar un carrito viejo (con menos items). El item sí
  // quedaba en el carrito real, pero el drawer mostraba 2 → el cliente reelegía.
  // Serializando las escrituras (cada add espera a que termine el anterior), el
  // último snapshot aplicado SIEMPRE refleja el carrito real completo.
  let kitCartQueue = Promise.resolve();
  function kitEnqueue(job) {
    const next = kitCartQueue.then(job, job);
    kitCartQueue = next.catch(() => {});
    return next;
  }

  // ---------------------------------------------------------------
  // 0. UPSELL "Agranda tu kit" — solo en /collections/crea-tu-kit-serene-a-b
  // ---------------------------------------------------------------
  // Botón único compacto dentro del cart-drawer (antes del subtotal/CTAs)
  // que agrega el producto "Agranda tu kit" SIN redirigir y SIN cerrar el drawer.
  //
  // Guards:
  // - Pathname guard: solo en KIT_UPSELL_PATH.
  // - DOM guard: data-kit-50mlb-upsell único (no duplica).
  // - In-cart flag: fetch /cart.js en init para conocer estado inicial.
  // - Button state guards (.disabled, .is-loading) previenen doble click.
  // - Try/catch protector: si algo falla, nunca rompe el drawer.
  //
  // CRÍTICO — preservar clase `active` del drawer:
  // Shrine Pro marca el drawer abierto con class="...active...". El server-side
  // render NO añade `active` (server no sabe si está abierto). Si hacemos
  // target.className = src.className, perdemos `active` y el drawer se cierra.
  // Solución: solo togglear `is-empty` (lo único que depende del contenido),
  // preservar todo lo demás.
  const KIT_UPSELL_PATH = '/collections/crea-tu-kit-serene-a-b';
  const KIT_UPSELL_PRODUCT_ID = 8347225456674;
  const KIT_UPSELL_VARIANT_ID = 45098367221794;
  const KIT_UPSELL_LABEL_DEFAULT = 'AGRANDA TU KIT  ·  +$50.000';
  const KIT_UPSELL_LABEL_LOADING = 'AGREGANDO…';
  const KIT_UPSELL_LABEL_ADDED   = '✓ KIT AGRANDADO';
  const KIT_UPSELL_LABEL_ERROR   = 'REINTENTAR';
  let kitUpsellInCartFlag = false;

  function ensureKitUpsellStyles() {
    if (document.getElementById('kit-50mlb-upsell-styles')) return;
    const s = document.createElement('style');
    s.id = 'kit-50mlb-upsell-styles';
    s.textContent = '.kit-50mlb-upsell{margin:.85rem 0;font-family:inherit}'
      + '.kit-50mlb-upsell__note{margin:0 0 .55rem;font-family:inherit;font-size:13px;line-height:1.4;font-weight:500;color:#5F5F5F;text-align:center;letter-spacing:.005em}'
      + '.kit-50mlb-upsell__btn{display:block;width:100%;padding:.95rem 1rem;background:#0B1F4D;color:#fff;border:1px solid #061436;border-radius:8px;font-family:inherit;font-size:16px;font-weight:800;letter-spacing:.04em;text-transform:uppercase;cursor:pointer;box-shadow:0 2px 4px rgba(11,31,77,.25);transition:background .15s,transform .1s,opacity .15s;animation:kit-50mlb-pulse 2.4s ease-in-out infinite}'
      + '.kit-50mlb-upsell__btn:hover:not(:disabled){background:#1B3475}'
      + '.kit-50mlb-upsell__btn:active:not(:disabled){transform:scale(.99)}'
      + '.kit-50mlb-upsell__btn:disabled{background:#7A8B6A;border-color:#677A58;opacity:1;cursor:default;box-shadow:none;animation:none}'
      + '.kit-50mlb-upsell__btn.is-loading{opacity:.85;cursor:wait;animation:none}'
      + '@keyframes kit-50mlb-pulse{0%,100%{box-shadow:0 2px 4px rgba(11,31,77,.25),0 0 0 0 rgba(11,31,77,.45)}50%{box-shadow:0 2px 4px rgba(11,31,77,.25),0 0 0 8px rgba(11,31,77,0)}}'
      + '@media (prefers-reduced-motion:reduce){.kit-50mlb-upsell__btn{animation:none}}';
    document.head.appendChild(s);
  }

  function buildKitUpsellHTML(inCart) {
    const btnAttrs = inCart ? 'disabled' : '';
    const btnLabel = inCart ? KIT_UPSELL_LABEL_ADDED : KIT_UPSELL_LABEL_DEFAULT;
    return '<div class="kit-50mlb-upsell" data-kit-50mlb-upsell>'
      +   '<p class="kit-50mlb-upsell__note">Cambia tus 3 fragancias de 50 ml por 3 fragancias de 100 ml.</p>'
      +   '<button type="button" class="kit-50mlb-upsell__btn" data-kit-50mlb-upsell-btn ' + btnAttrs + '>'
      +     btnLabel
      +   '</button>'
      + '</div>';
  }

  function injectKitUpsell(drawerEl) {
    try {
      if (!drawerEl) return;
      if (!window.location.pathname.startsWith(KIT_UPSELL_PATH)) return;
      if (drawerEl.querySelector('[data-kit-50mlb-upsell]')) return;
      const totals = drawerEl.querySelector('.cart-drawer__totals');
      if (!totals || !totals.parentNode) return;
      ensureKitUpsellStyles();
      const tpl = document.createElement('template');
      tpl.innerHTML = buildKitUpsellHTML(kitUpsellInCartFlag).trim();
      totals.parentNode.insertBefore(tpl.content.firstChild, totals);
    } catch (e) { /* noop — nunca romper el drawer */ }
  }

  function markUpsellAsAdded() {
    kitUpsellInCartFlag = true;
    const btn = document.querySelector('[data-kit-50mlb-upsell-btn]');
    if (btn) {
      btn.classList.remove('is-loading');
      btn.disabled = true;
      btn.textContent = KIT_UPSELL_LABEL_ADDED;
    }
  }

  async function syncKitUpsellInCart() {
    // Conocer estado inicial del flag consultando /cart.js
    if (!window.location.pathname.startsWith(KIT_UPSELL_PATH)) return;
    try {
      const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!r.ok) return;
      const cart = await r.json();
      kitUpsellInCartFlag = (cart.items || []).some((i) => i.product_id === KIT_UPSELL_PRODUCT_ID);
      // Re-render el botón si ya está agregado al cargar la página
      if (kitUpsellInCartFlag) markUpsellAsAdded();
    } catch (e) { /* noop */ }
  }

  async function handleKitUpsellAdd(btn) {
    if (!btn || btn.disabled || btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    btn.textContent = KIT_UPSELL_LABEL_LOADING;
    try {
      await kitEnqueue(async () => {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: KIT_UPSELL_VARIANT_ID, quantity: 1 }],
          sections: 'cart-icon-bubble,cart-drawer',
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || ('cart/add HTTP ' + res.status));
      }
      const data = await res.json();
      if (data.sections) {
        // cart-icon-bubble
        if (data.sections['cart-icon-bubble']) {
          const parsedBubble = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
          const tgtBubble = document.getElementById('cart-icon-bubble');
          const srcBubble = parsedBubble.querySelector('#shopify-section-cart-icon-bubble') || parsedBubble.querySelector('.shopify-section');
          if (tgtBubble && srcBubble) tgtBubble.innerHTML = srcBubble.innerHTML;
        }
        // cart-drawer — CRÍTICO: NO sobrescribir className, solo togglear is-empty.
        // Eso preserva la clase `active` (drawer abierto) y demás estado UI.
        if (data.sections['cart-drawer']) {
          const parsedDrawer = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');
          const tgtDrawer = document.querySelector('cart-drawer');
          const srcDrawer = parsedDrawer.querySelector('cart-drawer');
          if (tgtDrawer && srcDrawer) {
            tgtDrawer.classList.toggle('is-empty', srcDrawer.classList.contains('is-empty'));
            tgtDrawer.innerHTML = srcDrawer.innerHTML;
            injectKitUpsell(tgtDrawer);
          }
        }
      }
      });
      // Marcar el botón como agregado (no dependemos de DOM detection)
      markUpsellAsAdded();
    } catch (e) {
      console.error('[kit-50mlb-upsell] add error:', e);
      btn.classList.remove('is-loading');
      btn.disabled = false;
      btn.textContent = KIT_UPSELL_LABEL_ERROR;
    }
  }

  // Event delegation: capta clicks en cualquier instancia futura del botón
  // (el upsell se re-renderiza después de cada cart-drawer refresh).
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-kit-50mlb-upsell-btn]');
    if (!btn) return;
    e.preventDefault();
    handleKitUpsellAdd(btn);
  });

  // ---------------------------------------------------------------
  // 1. STATE MANAGER (singleton)
  // ---------------------------------------------------------------
  const STORAGE_KEY = 'serene_kit_50ml_state';

  const KitBuilder = {
    config: {
      target: 3,
      comboPrice: 16890000,        // 168.900 COP en centavos (fallback; la section kit-progress-bar lo sobreescribe desde el template)
      regularPrice: 32970000,      // 329.700 COP en centavos (3 × $109.900, precio regular tachado)
      currency: 'COP',
      celebrationShown: false,
    },

    events: new EventTarget(),

    getState() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { items: [] };
        const parsed = JSON.parse(raw);
        return parsed && Array.isArray(parsed.items) ? parsed : { items: [] };
      } catch (e) {
        return { items: [] };
      }
    },

    setState(state) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch (e) {
        // quota exceeded / safari privado: silenciar
      }
      this.events.dispatchEvent(new CustomEvent('kit:change', { detail: state }));
    },

    addItem(item) {
      const state = this.getState();
      state.items.push({
        variantId: item.variantId,
        productId: item.productId,
        title: item.title,
        image: item.image,
        price: item.price,
        addedAt: Date.now(),
      });
      this.setState(state);

      if (state.items.length === this.config.target && !this.config.celebrationShown) {
        this.config.celebrationShown = true;
        this.events.dispatchEvent(new CustomEvent('kit:complete', { detail: state }));
      }
    },

    clear() {
      this.setState({ items: [] });
      this.config.celebrationShown = false;
    },

    hasProduct(productId) {
      return this.getState().items.some((i) => String(i.productId) === String(productId));
    },

    getCount() {
      return this.getState().items.length;
    },

    /**
     * Sincroniza el estado local contra /cart.js. Si el carrito real está vacío
     * (el cliente lo vació en otra pestaña o abre en una sesión nueva), limpia
     * el estado para que la barra no muestre "Kit completo" sin razón.
     */
    async syncWithCart() {
      try {
        const res = await fetch('/cart.js', {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!res.ok) return;
        const cart = await res.json();
        const cartVariantIds = (cart.items || []).map((i) => String(i.variant_id));
        const state = this.getState();

        // Cart vacío → reset total del kit
        if (cartVariantIds.length === 0 && state.items.length > 0) {
          this.clear();
          return;
        }

        // Filtrar items del kit que ya no están en el cart real
        const validItems = state.items.filter((i) => cartVariantIds.includes(String(i.variantId)));
        if (validItems.length !== state.items.length) {
          this.setState({ items: validItems });
        }
        // Reset del flag de celebración si ya no está completo
        if (validItems.length < this.config.target) {
          this.config.celebrationShown = false;
        }
      } catch (e) {
        // offline o endpoint caído: no romper UX
      }
    },

    formatPrice(cents) {
      try {
        return new Intl.NumberFormat('es-CO', {
          style: 'currency',
          currency: this.config.currency,
          maximumFractionDigits: 0,
        }).format(cents / 100);
      } catch (e) {
        return '$' + Math.round(cents / 100).toLocaleString('es-CO');
      }
    },
  };

  window.KitBuilder = KitBuilder;

  // ---------------------------------------------------------------
  // 2. <kit-add-button>  → botón en la card
  // ---------------------------------------------------------------
  class KitAddButton extends HTMLElement {
    connectedCallback() {
      this.productId = this.dataset.productId;
      this.productUrl = this.dataset.productUrl;
      this.button = this.querySelector('button');
      if (!this.button) return;

      this.button.addEventListener('click', this.handleClick.bind(this));
      this.onStateChange = this.render.bind(this);
      KitBuilder.events.addEventListener('kit:change', this.onStateChange);
      this.render();
    }

    disconnectedCallback() {
      KitBuilder.events.removeEventListener('kit:change', this.onStateChange);
    }

    async handleClick(e) {
      e.preventDefault();
      if (this.button.disabled || this.button.classList.contains('is-loading')) return;

      const label = this.button.querySelector('.kit-add-button__label');
      const originalLabel = label ? label.textContent : 'Sumar al kit';
      this.button.classList.add('is-loading');
      this.button.disabled = true;
      if (label) label.textContent = 'Agregando…';

      try {
        // 1. variant_id del producto desde /products/{handle}.js
        const productPath = (this.productUrl || '').split('?')[0].replace(/\/+$/, '');
        if (!productPath) throw new Error('Sin product URL');
        const pjRes = await fetch(productPath + '.js', { headers: { Accept: 'application/json' } });
        if (!pjRes.ok) throw new Error('No se pudo obtener variante (' + pjRes.status + ')');
        const product = await pjRes.json();
        const variant = (product.variants || []).find((v) => v.available) || (product.variants || [])[0];
        if (!variant) throw new Error('Sin variante disponible');

        // 2-4. Serializado en la cola (kitEnqueue) para evitar el race de
        //      snapshots del cart-drawer cuando se tocan varias cards seguidas.
        await kitEnqueue(async () => {
        // 2. POST /cart/add.js — incluye sections para refresco server-rendered
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variant.id, quantity: 1 }],
            sections: 'cart-icon-bubble,cart-drawer',
          }),
        });
        if (!addRes.ok) {
          const err = await addRes.json().catch(() => ({}));
          throw new Error(err.description || ('cart/add HTTP ' + addRes.status));
        }
        const data = await addRes.json();

        // 3. Actualizar estado del KitBuilder (dispara kit:change → barra de progreso)
        KitBuilder.addItem({
          variantId: variant.id,
          productId: product.id,
          title: product.title,
          image: (product.featured_image || '').replace(/_\d+x\d*(@2x)?\./, '_200x.'),
          price: variant.price,
        });

        // 4. Refrescar sections server-rendered (cart-icon-bubble + cart-drawer)
        if (data.sections) {
          Object.keys(data.sections).forEach((id) => {
            const html = data.sections[id];
            if (!html) return;
            try {
              const parsed = new DOMParser().parseFromString(html, 'text/html');
              if (id === 'cart-icon-bubble') {
                const target = document.getElementById('cart-icon-bubble');
                const src = parsed.querySelector('#shopify-section-cart-icon-bubble') || parsed.querySelector('.shopify-section');
                if (target && src) target.innerHTML = src.innerHTML;
              } else if (id === 'cart-drawer') {
                const target = document.querySelector('cart-drawer');
                const src = parsed.querySelector('cart-drawer');
                if (target && src) {
                  // CRÍTICO: solo togglear `is-empty` (lo único que depende del
                  // contenido del carrito). NO sobrescribir className completo —
                  // perdería la clase `active` que mantiene el drawer abierto
                  // en Shrine Pro. El server-side render NO incluye `active`,
                  // así que copiar todo cerraría el drawer si está abierto.
                  target.classList.toggle('is-empty', src.classList.contains('is-empty'));
                  target.innerHTML = src.innerHTML;
                  // Re-inyectar upsell tras el re-render (solo en /collections/crea-tu-kit-serene-a-b)
                  injectKitUpsell(target);
                }
              }
            } catch (e) { /* noop */ }
          });
        }
        });

        // 5. Feedback visual (✓ Agregado por ~900ms)
        if (label) label.textContent = '✓ Agregado';
        this.button.classList.remove('is-loading');
        this.button.classList.add('is-success');
        setTimeout(() => {
          this.button.classList.remove('is-success');
          this.button.disabled = false;
          // render() (via kit:change) ya repinta el label correcto: 'Sumar al kit' o 'Agregar otra vez'
        }, 900);
      } catch (err) {
        console.error('[kit-add-button] error:', err);
        this.button.classList.remove('is-loading');
        this.button.disabled = false;
        if (label) label.textContent = 'Error, reintentá';
        setTimeout(() => { if (label) label.textContent = originalLabel; }, 1800);
      }
    }

    render() {
      const inKit = KitBuilder.hasProduct(this.productId);
      this.classList.toggle('kit-add-button--in-kit', inKit);
      const label = this.button.querySelector('.kit-add-button__label');
      if (label) {
        label.textContent = inKit ? 'Agregar otra vez' : 'Sumar al kit';
      }
    }
  }
  if (!customElements.get('kit-add-button')) {
    customElements.define('kit-add-button', KitAddButton);
  }

  // ---------------------------------------------------------------
  // 3. <kit-quick-add-modal>  → modal slim con fetch JSON
  // ---------------------------------------------------------------
  class KitQuickAddModal extends HTMLElement {
    constructor() {
      super();
      this.content = this.querySelector('.kit-modal__content');
      this.overlay = this.querySelector('.kit-modal__overlay');
      this.closeBtn = this.querySelector('.kit-modal__close');
      this.body = this.querySelector('.kit-modal__body');
      this.lastFocus = null;
      this.focusableSelector =
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

      this.addEventListener('keydown', this.handleKeydown.bind(this));
      if (this.overlay) this.overlay.addEventListener('click', this.hide.bind(this));
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.hide.bind(this));

      // Delegación de eventos: un solo listener sobre el body del modal.
      // Sobrevive a innerHTML rehydrations, garantiza que el click del CTA
      // siempre se dispare aunque el markup interno se reescriba.
      if (this.body) {
        this.body.addEventListener('click', this.handleBodyClick.bind(this));
        this.body.addEventListener('change', this.handleBodyChange.bind(this));
        this.body.addEventListener('input', this.handleBodyInput.bind(this));
      }
    }

    handleBodyClick(e) {
      const submit = e.target.closest('[data-kit-submit]');
      if (submit) {
        e.preventDefault();
        this.handleSubmit({ currentTarget: submit });
        return;
      }
      const qtyBtn = e.target.closest('.kit-modal__qty-btn');
      if (qtyBtn) {
        e.preventDefault();
        const qtyInput = this.body.querySelector('.kit-modal__qty-input');
        if (!qtyInput) return;
        let v = parseInt(qtyInput.value, 10) || 1;
        v = qtyBtn.dataset.qty === 'plus' ? v + 1 : v - 1;
        v = Math.min(10, Math.max(1, v));
        qtyInput.value = v;
        this.quantity = v;
        return;
      }
      const errClose = e.target.closest('.kit-modal__error-close');
      if (errClose) {
        this.hide();
        return;
      }
    }

    handleBodyChange(e) {
      const variantRadio = e.target.closest('.kit-modal__variant-group input[type="radio"]');
      if (variantRadio) {
        this.handleVariantChange();
      }
      const qtyInput = e.target.closest('.kit-modal__qty-input');
      if (qtyInput) {
        let v = parseInt(qtyInput.value, 10) || 1;
        v = Math.min(10, Math.max(1, v));
        qtyInput.value = v;
        this.quantity = v;
      }
    }

    handleBodyInput(e) {
      const qtyInput = e.target.closest('.kit-modal__qty-input');
      if (qtyInput) {
        const v = parseInt(qtyInput.value, 10);
        if (!isNaN(v) && v >= 1 && v <= 10) this.quantity = v;
      }
    }

    connectedCallback() {
      // Mover el modal al <body> directamente para escapar cualquier ancestro
      // con transform/filter/clip-path que rompa el position: fixed.
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
      }
    }

    async show({ productUrl, productId, opener }) {
      this.lastFocus = opener || document.activeElement;
      this.body.innerHTML = this.renderSkeleton();
      this.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';

      try {
        const handle = productUrl.split('/products/')[1].split('?')[0].split('/')[0];
        const res = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('Producto no disponible');
        const product = await res.json();
        this.product = product;
        this.selectedVariant = product.variants.find((v) => v.available) || product.variants[0];
        this.quantity = 1;
        this.renderProduct();
        this.trapFocus();
      } catch (e) {
        this.body.innerHTML = this.renderError();
      }
    }

    hide() {
      this.removeAttribute('open');
      document.documentElement.style.overflow = '';
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
      this.product = null;
    }

    handleKeydown(e) {
      if (!this.hasAttribute('open')) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        this.hide();
      } else if (e.key === 'Tab') {
        const focusables = this.content.querySelectorAll(this.focusableSelector);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    trapFocus() {
      const focusables = this.content.querySelectorAll(this.focusableSelector);
      const first = focusables[0] || this.closeBtn;
      if (first) first.focus();
    }

    renderSkeleton() {
      return `
        <div class="kit-modal__skeleton" aria-label="Cargando producto">
          <div class="kit-modal__skeleton-img"></div>
          <div class="kit-modal__skeleton-line kit-modal__skeleton-line--lg"></div>
          <div class="kit-modal__skeleton-line"></div>
          <div class="kit-modal__skeleton-line kit-modal__skeleton-line--sm"></div>
        </div>`;
    }

    renderError() {
      return `
        <div class="kit-modal__error">
          <p>No pudimos cargar este producto. Intentá de nuevo.</p>
          <button type="button" class="kit-modal__error-close button button--primary">Cerrar</button>
        </div>`;
    }

    renderProduct() {
      const p = this.product;
      const v = this.selectedVariant;
      const count = KitBuilder.getCount();
      const target = KitBuilder.config.target;
      const remaining = Math.max(0, target - count);
      const images = (p.images && p.images.length
        ? p.images
        : (p.featured_image ? [p.featured_image] : [])
      ).map(src => src.replace(/_\d+x\d*(@2x)?\./, '_600x.'));

      const variantHtml = p.variants.length > 1 ? this.renderVariants() : '';
      const priceFormatted = KitBuilder.formatPrice(v.price);
      const descHtml = this.buildDescription(p);

      // Parse título para mostrar jerarquía: SKU + fragancia + marca
      const header = this.parseTitle(p.title);

      // Microcopy por estado del kit
      let remainingCopy, remainingState;
      if (count === 0) {
        remainingCopy = `Elegí <strong>${target}</strong> fragancias para armar tu kit`;
        remainingState = 'start';
      } else if (remaining > 0) {
        remainingCopy = `Te falta${remaining > 1 ? 'n' : ''} <strong>${remaining}</strong> para completar el kit de ${target}`;
        remainingState = 'progress';
      } else {
        remainingCopy = `Tu kit de ${target} ya está listo. Podés sumar más o ir al carrito.`;
        remainingState = 'complete';
      }

      const dotsHtml = images.length > 1 ? `
        <div class="kit-modal__dots" role="tablist" aria-label="Imágenes del producto">
          ${images.map((_, i) => `<button class="kit-modal__dot${i === 0 ? ' is-active' : ''}" type="button" role="tab" aria-label="Imagen ${i + 1} de ${images.length}" aria-selected="${i === 0}" data-slide="${i}"></button>`).join('')}
        </div>` : '';

      this.body.innerHTML = `
        <div class="kit-modal__product">
          <div class="kit-modal__media-wrap">
            <div class="kit-modal__media" data-image-count="${images.length}">
              ${images.map((src, i) => `<img src="${src}" alt="${this.escape(p.title)}" ${i === 0 ? 'loading="eager"' : 'loading="lazy"'} width="600" height="600">`).join('')}
            </div>
            ${dotsHtml}
          </div>
          <div class="kit-modal__info">
            <header class="kit-modal__header">
              ${header.sku ? `<span class="kit-modal__sku">${this.escape(header.sku)}</span>` : ''}
              <h2 class="kit-modal__title" id="kit-modal-title">${this.escape(header.name || p.title)}</h2>
              ${header.brand ? `<p class="kit-modal__brand">Inspirada en ${this.escape(header.brand)}</p>` : ''}
            </header>
            <div class="kit-modal__price">
              <div class="kit-modal__price-main">
                <span class="kit-modal__price-unit" data-price-display>${priceFormatted}</span>
                <span class="kit-modal__price-label">por unidad · 50 ml</span>
              </div>
              <div class="kit-modal__price-combo">
                <svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" width="13" height="13"><path d="M2 4h12l-1 8H3L2 4z M6 4V2.5A1.5 1.5 0 0 1 7.5 1h1A1.5 1.5 0 0 1 10 2.5V4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <span>3 x ${KitBuilder.formatPrice(KitBuilder.config.comboPrice)} · arma tu kit completo</span>
              </div>
            </div>
            ${descHtml}
            ${variantHtml}
            <div class="kit-modal__qty">
              <label class="kit-modal__qty-label" for="kit-modal-qty">Cantidad</label>
              <div class="kit-modal__qty-control">
                <button type="button" class="kit-modal__qty-btn" data-qty="minus" aria-label="Quitar una unidad">−</button>
                <input id="kit-modal-qty" class="kit-modal__qty-input" type="number" min="1" max="10" value="${this.quantity}" inputmode="numeric">
                <button type="button" class="kit-modal__qty-btn" data-qty="plus" aria-label="Sumar una unidad">+</button>
              </div>
            </div>
            <div class="kit-modal__remaining" data-state="${remainingState}" aria-live="polite">${remainingCopy}</div>
            <button type="button" class="kit-modal__cta button button--primary" data-kit-submit>
              <svg class="kit-modal__cta-icon" aria-hidden="true" focusable="false" viewBox="0 0 20 20" width="18" height="18">
                <path d="M3 5h2l2 9h9l2-7H6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                <circle cx="9" cy="17" r="1.2" fill="currentColor"/>
                <circle cx="15" cy="17" r="1.2" fill="currentColor"/>
              </svg>
              <span class="kit-modal__cta-label">Agregar al kit</span>
              <span class="kit-modal__cta-price">${priceFormatted}</span>
              <svg class="kit-modal__cta-spinner" aria-hidden="true" focusable="false" viewBox="0 0 66 66" width="20" height="20">
                <circle cx="33" cy="33" r="30" stroke="currentColor" stroke-width="6" fill="none"></circle>
              </svg>
            </button>
            <ul class="kit-modal__benefits" aria-label="Beneficios">
              <li>
                <svg aria-hidden="true" focusable="false" viewBox="0 0 20 20" width="14" height="14"><path d="M2 14V7h9v7M11 9h4l2 3v2h-6M4 14a2 2 0 1 0 4 0 2 2 0 0 0-4 0zM13 14a2 2 0 1 0 4 0 2 2 0 0 0-4 0z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
                Envío gratis en Colombia
              </li>
              <li>
                <svg aria-hidden="true" focusable="false" viewBox="0 0 20 20" width="14" height="14"><path d="M4 7l6-3 6 3v6l-6 3-6-3V7zM4 7l6 3 6-3M10 10v6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
                Stock disponible
              </li>
            </ul>
          </div>
        </div>
      `;

      // Sincronizar dots del carousel con el scroll
      if (images.length > 1) this.bindCarouselDots();
    }

    /**
     * Extrae SKU, nombre de fragancia y marca del título del producto.
     * Titulos típicos: "Serené Le Parfum S032 - Inspirada en Silver Mountain Water por Creed (Kit 50 ml)"
     */
    parseTitle(title) {
      if (!title) return { sku: '', name: '', brand: '' };
      const skuMatch = title.match(/\bS\d{3,4}\b/);
      const sku = skuMatch ? skuMatch[0] : '';
      // Quitar "(Kit 50 ml)" y variantes del final
      let clean = title.replace(/\s*\(Kit\s*50\s*(ml|ML)\)\s*$/i, '').trim();
      // Intentar separar "Inspirada en X por/By Y"
      const m = clean.match(/[Ii]nspirad[ao]\s+en:?\s*(.+?)\s+(?:por|[Bb]y)\s+(.+)$/);
      if (m) {
        return { sku, name: m[1].trim(), brand: m[2].trim() };
      }
      // Fallback: solo intenta "Inspirada en X"
      const m2 = clean.match(/[Ii]nspirad[ao]\s+en:?\s*(.+)$/);
      if (m2) {
        return { sku, name: m2[1].trim(), brand: '' };
      }
      return { sku, name: clean, brand: '' };
    }

    /**
     * Sincroniza los dots del carousel con el scroll horizontal del media.
     * Al tocar un dot, hace scroll al slide correspondiente.
     */
    bindCarouselDots() {
      const media = this.body.querySelector('.kit-modal__media');
      const dots = Array.from(this.body.querySelectorAll('.kit-modal__dot'));
      if (!media || dots.length === 0) return;
      // Click en dot → scroll al slide
      dots.forEach((dot, idx) => {
        dot.addEventListener('click', () => {
          const slideWidth = media.clientWidth;
          media.scrollTo({ left: slideWidth * idx, behavior: 'smooth' });
        });
      });
      // Scroll en el media → actualiza dot activo
      let raf = null;
      media.addEventListener('scroll', () => {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const idx = Math.round(media.scrollLeft / Math.max(1, media.clientWidth));
          dots.forEach((d, i) => {
            const active = i === idx;
            d.classList.toggle('is-active', active);
            d.setAttribute('aria-selected', active ? 'true' : 'false');
          });
        });
      }, { passive: true });
    }

    /**
     * Construye el HTML de la descripción del producto.
     * Usa el HTML seguro devuelto por /products/{handle}.js pero quita scripts
     * y limita la altura con CSS para no romper el modal.
     */
    buildDescription(p) {
      const raw = p.description || '';
      if (!raw.trim()) return '';
      // Sanitize: eliminar scripts y style tags
      const clean = raw
        .replace(/<script[\s\S]*?<\/script>/gi, '')
        .replace(/<style[\s\S]*?<\/style>/gi, '')
        .replace(/on\w+\s*=\s*(['"]).*?\1/gi, '');
      return `<details class="kit-modal__desc-toggle">
        <summary class="kit-modal__desc-summary">
          <span class="kit-modal__desc-summary-label">Descripción del producto</span>
          <svg class="kit-modal__desc-caret" aria-hidden="true" focusable="false" viewBox="0 0 16 16" width="14" height="14">
            <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </summary>
        <div class="kit-modal__description">${clean}</div>
      </details>`;
    }

    renderVariants() {
      const p = this.product;
      const options = p.options.map((name, idx) => {
        const values = [...new Set(p.variants.map((v) => v.options[idx]).filter(Boolean))];
        if (values.length <= 1) return '';
        return `
          <fieldset class="kit-modal__variant-group" data-option-index="${idx}">
            <legend class="kit-modal__variant-legend">${this.escape(name)}</legend>
            <div class="kit-modal__variant-options">
              ${values
                .map((val) => {
                  const checked = this.selectedVariant.options[idx] === val ? 'checked' : '';
                  return `
                    <label class="kit-modal__variant-option">
                      <input type="radio" name="option-${idx}" value="${this.escape(val)}" ${checked}>
                      <span>${this.escape(val)}</span>
                    </label>`;
                })
                .join('')}
            </div>
          </fieldset>`;
      });
      return options.join('');
    }

    // NOTA: el viejo bindProductEvents() se reemplazó por delegación de eventos
    // en el constructor (handleBodyClick, handleBodyChange, handleBodyInput).
    // De esta forma, cada vez que renderProduct() reescribe el innerHTML,
    // los listeners siguen funcionando sin necesidad de re-bindear.

    handleVariantChange() {
      const groups = this.body.querySelectorAll('.kit-modal__variant-group');
      const selectedOptions = [];
      groups.forEach((g) => {
        const checked = g.querySelector('input[type="radio"]:checked');
        if (checked) selectedOptions.push(checked.value);
      });

      const match = this.product.variants.find((v) => {
        return selectedOptions.every((opt, idx) => v.options[idx] === opt);
      });

      if (match) {
        this.selectedVariant = match;
        const priceEl = this.body.querySelector('[data-price-display]');
        const ctaLabel = this.body.querySelector('.kit-modal__cta-label');
        const priceFormatted = KitBuilder.formatPrice(match.price);
        if (priceEl) priceEl.textContent = priceFormatted;
        if (ctaLabel) ctaLabel.textContent = `Agregar al kit · ${priceFormatted}`;

        const submit = this.body.querySelector('[data-kit-submit]');
        if (submit) {
          submit.disabled = !match.available;
          if (!match.available) {
            const label = submit.querySelector('.kit-modal__cta-label');
            if (label) label.textContent = 'Agotado';
          }
        }
      }
    }

    async handleSubmit(e) {
      const btn = e.currentTarget;
      if (btn.disabled || btn.classList.contains('is-loading')) return;
      btn.classList.add('is-loading');
      btn.disabled = true;

      try {
        await kitEnqueue(async () => {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: this.selectedVariant.id, quantity: this.quantity }],
            sections: 'cart-icon-bubble,cart-drawer',
          }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || 'No se pudo agregar');
        }

        const data = await res.json();

        // 1. guardar en el kit
        KitBuilder.addItem({
          variantId: this.selectedVariant.id,
          productId: this.product.id,
          title: this.product.title,
          image: (this.product.featured_image || '').replace(/_\d+x\d*(@2x)?\./, '_200x.'),
          price: this.selectedVariant.price,
        });

        // 2. refrescar sections server-rendered (cart-icon-bubble, cart-drawer)
        if (data.sections) {
          this.updateSections(data.sections);
        }
        });

        // 3. feedback visual en el botón
        const label = btn.querySelector('.kit-modal__cta-label');
        if (label) label.textContent = '✓ Agregado';
        btn.classList.remove('is-loading');
        btn.classList.add('is-success');

        // 4. auto cerrar y (si se completó) disparar celebración
        setTimeout(() => {
          this.hide();
          btn.classList.remove('is-success');
        }, 700);
      } catch (err) {
        btn.classList.remove('is-loading');
        btn.disabled = false;
        const label = btn.querySelector('.kit-modal__cta-label');
        if (label) label.textContent = 'Error, reintentá';
        setTimeout(() => {
          if (label) label.textContent = `Agregar al kit · ${KitBuilder.formatPrice(this.selectedVariant.price)}`;
        }, 1800);
      }
    }

    updateSections(sections) {
      Object.keys(sections).forEach((id) => {
        const html = sections[id];
        if (!html) return;
        try {
          const parsed = new DOMParser().parseFromString(html, 'text/html');

          // cart-icon-bubble
          if (id === 'cart-icon-bubble') {
            const target = document.getElementById('cart-icon-bubble');
            const src = parsed.querySelector('#shopify-section-cart-icon-bubble') || parsed.querySelector('.shopify-section');
            if (target && src) target.innerHTML = src.innerHTML;
          }

          // cart-drawer — preservar `active`, solo togglear is-empty (igual que el botón de card)
          if (id === 'cart-drawer') {
            const target = document.querySelector('cart-drawer');
            const src = parsed.querySelector('cart-drawer');
            if (target && src) {
              target.classList.toggle('is-empty', src.classList.contains('is-empty'));
              target.innerHTML = src.innerHTML;
              injectKitUpsell(target);
            }
          }
        } catch (e) {
          // fail safe
        }
      });
    }

    escape(s) {
      return String(s).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      }[c]));
    }
  }
  if (!customElements.get('kit-quick-add-modal')) {
    customElements.define('kit-quick-add-modal', KitQuickAddModal);
  }

  // ---------------------------------------------------------------
  // 4. <kit-progress-bar>  → barra sticky superior
  // ---------------------------------------------------------------
  class KitProgressBar extends HTMLElement {
    connectedCallback() {
      this.countEl = this.querySelector('[data-kit-count]');
      this.targetEl = this.querySelector('[data-kit-target]');
      this.fillEl = this.querySelector('[data-kit-fill]');
      this.statusEl = this.querySelector('[data-kit-status]');
      this.currentPriceEl = this.querySelector('[data-kit-current-price]');
      this.comboPriceEl = this.querySelector('[data-kit-combo-price]');
      this.resetBtn = this.querySelector('[data-kit-reset]');

      this.onStateChange = this.render.bind(this);
      this.onComplete = this.celebrate.bind(this);
      KitBuilder.events.addEventListener('kit:change', this.onStateChange);
      KitBuilder.events.addEventListener('kit:complete', this.onComplete);

      if (this.resetBtn) {
        this.resetBtn.addEventListener('click', () => {
          if (KitBuilder.getCount() === 0) return;
          const ok = window.confirm('¿Querés vaciar tu kit y empezar de nuevo?');
          if (ok) KitBuilder.clear();
        });
      }
      this.render();
    }

    disconnectedCallback() {
      KitBuilder.events.removeEventListener('kit:change', this.onStateChange);
      KitBuilder.events.removeEventListener('kit:complete', this.onComplete);
    }

    render() {
      const state = KitBuilder.getState();
      const count = state.items.length;
      const target = KitBuilder.config.target;
      const progress = Math.min(100, (count / target) * 100);

      if (this.countEl) this.countEl.textContent = count;
      if (this.targetEl) this.targetEl.textContent = target;
      if (this.fillEl) this.fillEl.style.width = progress + '%';

      this.classList.toggle('kit-progress--complete', count >= target);
      this.classList.toggle('kit-progress--empty', count === 0);

      if (this.statusEl) {
        if (count === 0) {
          this.statusEl.textContent = `Escogé tus ${target} fragancias favoritas`;
        } else if (count < target) {
          const falta = target - count;
          this.statusEl.textContent = `Te falta${falta > 1 ? 'n' : ''} ${falta} para completar tu kit`;
        } else {
          this.statusEl.textContent = `¡Kit completo! Revisá tu carrito`;
        }
      }

      if (this.resetBtn) {
        this.resetBtn.style.display = count > 0 ? 'inline-flex' : 'none';
      }

      if (this.currentPriceEl) {
        const current = count * (KitBuilder.config.regularPrice / target);
        this.currentPriceEl.textContent = KitBuilder.formatPrice(current);
      }
    }

    celebrate() {
      this.classList.add('kit-progress--celebrating');
      setTimeout(() => this.classList.remove('kit-progress--celebrating'), 1800);
    }
  }
  if (!customElements.get('kit-progress-bar')) {
    customElements.define('kit-progress-bar', KitProgressBar);
  }

  // ---------------------------------------------------------------
  // 5. <kit-sticky-cta>  → CTA fijo inferior mobile
  // ---------------------------------------------------------------
  class KitStickyCta extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button, a');
      this.countEl = this.querySelector('[data-kit-count]');
      this.targetEl = this.querySelector('[data-kit-target]');
      this.labelEl = this.querySelector('[data-kit-cta-label]');
      this.cartUrl = this.dataset.cartUrl || '/cart';

      // 2026-06-04: gate de kit incompleto. Mensaje + estilos del botón
      // deshabilitado. Solo afecta esta colección (este JS no se comparte).
      this.ensureGateStyles();
      this.ensureWarnEl();

      this.onStateChange = this.render.bind(this);
      KitBuilder.events.addEventListener('kit:change', this.onStateChange);

      if (this.button) this.button.addEventListener('click', this.handleClick.bind(this));
      this.render();
    }

    disconnectedCallback() {
      KitBuilder.events.removeEventListener('kit:change', this.onStateChange);
    }

    // Inyecta una sola vez los estilos del estado deshabilitado + mensaje.
    ensureGateStyles() {
      if (document.getElementById('kit-50mlb-gate-styles')) return;
      const style = document.createElement('style');
      style.id = 'kit-50mlb-gate-styles';
      style.textContent =
        '.kit-sticky__btn--disabled{opacity:.5;cursor:not-allowed;filter:grayscale(.35)}' +
        '.kit-sticky__warn{margin:0 0 .5rem;font-size:13px;font-weight:600;line-height:1.35;color:#B42318;text-align:center;display:none}';
      document.head.appendChild(style);
    }

    // Crea (una vez) el contenedor del mensaje de advertencia, arriba del botón.
    ensureWarnEl() {
      if (this.warnEl) return;
      const p = document.createElement('p');
      p.className = 'kit-sticky__warn';
      p.setAttribute('role', 'alert');
      p.setAttribute('aria-live', 'assertive');
      this.insertBefore(p, this.firstChild);
      this.warnEl = p;
    }

    handleClick(e) {
      const count = KitBuilder.getCount();
      const target = KitBuilder.config.target;

      // GATE: con menos de `target` fragancias no se avanza al carrito.
      // El botón se mantiene clicable (aria-disabled, no disabled real) para
      // poder capturar el intento y mostrar el mensaje requerido.
      if (count < target) {
        e.preventDefault();
        if (this.warnEl) {
          this.warnEl.textContent =
            `Debes elegir ${target} fragancias para completar tu kit por $168.900.`;
          this.warnEl.style.display = 'block';
        }
        return;
      }

      // Kit completo (>= target): abrir el cart-drawer (o fallback a /cart).
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        e.preventDefault();
        drawer.open(this.button);
      } else if (this.button.tagName !== 'A') {
        e.preventDefault();
        window.location.href = this.cartUrl;
      }
      // si es <a href="/cart"> sin drawer, dejar el href nativo
    }

    render() {
      const count = KitBuilder.getCount();
      const target = KitBuilder.config.target;
      const complete = count >= target;

      this.classList.toggle('kit-sticky--visible', count > 0);
      this.classList.toggle('kit-sticky--complete', complete);

      // Estado deshabilitado hasta completar el kit. Usamos aria-disabled +
      // clase visual (NO el atributo `disabled` real) para que el click siga
      // disparando handleClick y muestre el mensaje al intentar avanzar.
      if (this.button) {
        this.button.setAttribute('aria-disabled', String(!complete));
        this.button.classList.toggle('kit-sticky__btn--disabled', !complete);
      }
      // Al completar, ocultar el mensaje de advertencia.
      if (complete && this.warnEl) this.warnEl.style.display = 'none';

      if (this.countEl) this.countEl.textContent = count;
      if (this.targetEl) this.targetEl.textContent = target;
      if (this.labelEl) {
        this.labelEl.textContent = complete
          ? `¡Kit completo! Ir al carrito`
          : `Ver mi kit (${count}/${target})`;
      }
    }
  }
  if (!customElements.get('kit-sticky-cta')) {
    customElements.define('kit-sticky-cta', KitStickyCta);
  }

  // ---------------------------------------------------------------
  // 6. Click delegation: imagen de la card abre el modal
  // ---------------------------------------------------------------
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-kit-open-modal]');
    if (!trigger) return;
    e.preventDefault();
    const modal = document.getElementById('KitQuickAddModal');
    if (!modal) return;
    modal.show({
      productUrl: trigger.dataset.productUrl,
      productId: trigger.dataset.productId,
      opener: trigger,
    });
  });

  // ---------------------------------------------------------------
  // 7. Card state sync: marca cards en el kit con clase + badge
  // ---------------------------------------------------------------
  const syncCards = () => {
    document.querySelectorAll('[data-kit-card]').forEach((card) => {
      const productId = card.dataset.productId;
      const inKit = KitBuilder.hasProduct(productId);
      card.classList.toggle('kit-card--in-kit', inKit);
      const badge = card.querySelector('[data-kit-card-badge]');
      if (badge) {
        badge.style.display = inKit ? 'inline-flex' : 'none';
      }
    });
  };
  KitBuilder.events.addEventListener('kit:change', syncCards);

  // ---------------------------------------------------------------
  // 8. Celebration: kit:complete listener
  // ---------------------------------------------------------------
  // 2026-05-20: auto-apertura RE-HABILITADA tras arreglar la sincronización
  // de className en el innerHTML replace (target.className = src.className).
  // Ahora el wrapper ya no queda con 'is-empty' colgado, así que el drawer
  // renderiza con contenido real al completar 3/3. Se usa requestAnimationFrame
  // para asegurar que el último replace de innerHTML haya pintado antes de abrir.
  KitBuilder.events.addEventListener('kit:complete', () => {
    requestAnimationFrame(() => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      }
    });
  });

  // ---------------------------------------------------------------
  // 9. Init: sync con cart real al cargar + render inicial
  // ---------------------------------------------------------------
  const init = () => {
    KitBuilder.syncWithCart();
    syncCards();
    // Inyectar upsell en el cart-drawer pre-renderizado server-side
    // (cuando el user llega a la colección con items previos en el carrito).
    injectKitUpsell(document.querySelector('cart-drawer'));
    // Consultar /cart.js para conocer si el upsell ya está agregado y
    // marcar el botón como "✓ KIT AGRANDADO" si corresponde.
    syncKitUpsellInCart();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
